import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Calendar, Phone, Download, RefreshCw, ExternalLink, MailCheck, MailX, MailWarning,
  Pencil, AlertTriangle, LayoutGrid, List,
} from 'lucide-react';
import ProspectDrawer from '@/components/ProspectDrawer';

const PERIOD_OPTIONS = [
  { value: 'upcoming', label: 'À venir' },
  { value: 'past', label: 'Passés' },
  { value: 'week', label: '7 derniers jours' },
  { value: 'month', label: '30 derniers jours' },
  { value: 'all', label: 'Tous' },
];

function formatRdvDate(iso) {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function formatSentAt(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 16).replace('T', ' '); }
}

function CalendarView({ items, loading, onOpen }) {
  // Use current month based on the first upcoming RDV, else today
  const [cursor, setCursor] = React.useState(() => {
    const firstUpcoming = items.find(r => r.rdv_date_iso && r.rdv_date_iso >= new Date().toISOString().slice(0, 10));
    const d = firstUpcoming ? new Date(firstUpcoming.rdv_date_iso) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  // Monday-first weekday: 0=Mon, 6=Sun
  const startWeekday = (firstDayOfMonth.getDay() + 6) % 7;
  const daysInMonth = lastDayOfMonth.getDate();

  // Group items by rdv_date_iso
  const byDate = {};
  items.forEach(r => {
    if (!r.rdv_date_iso) return;
    if (!byDate[r.rdv_date_iso]) byDate[r.rdv_date_iso] = [];
    byDate[r.rdv_date_iso].push(r);
  });

  const todayIso = new Date().toISOString().slice(0, 10);
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, iso, rdvs: byDate[iso] || [], isToday: iso === todayIso });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = cursor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <Card className="border-[#E5E7EB]">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold capitalize">{monthLabel}</h3>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="calendar-prev-btn">‹</Button>
            <Button size="sm" variant="outline" onClick={() => setCursor(new Date())} data-testid="calendar-today-btn">Aujourd'hui</Button>
            <Button size="sm" variant="outline" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="calendar-next-btn">›</Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-xs text-center text-[#71717A] font-medium mb-1">
          {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => <div key={i} className="py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) => (
            <div key={i} className={`min-h-[100px] rounded border ${c ? 'border-[#E5E7EB] bg-white' : 'border-transparent'} ${c?.isToday ? 'ring-2 ring-[#F97316] ring-offset-1' : ''} p-1 overflow-hidden`}
              data-testid={c ? `calendar-day-${c.iso}` : null}>
              {c && (
                <>
                  <div className={`text-[11px] font-semibold ${c.isToday ? 'text-[#F97316]' : 'text-[#71717A]'}`}>{c.day}</div>
                  <div className="space-y-0.5 mt-1">
                    {c.rdvs.slice(0, 3).map(r => (
                      <button key={r.prospect_id} onClick={() => onOpen(r.prospect_id)}
                        className="block w-full text-left text-[10px] px-1 py-0.5 rounded truncate hover:opacity-80"
                        style={{ background: (r.commercial_color || '#71717A') + '22', color: r.commercial_color || '#71717A', borderLeft: `2px solid ${r.commercial_color || '#71717A'}` }}
                        title={`${r.enseigne} ${r.nom_magasin} · ${r.ville}`}
                        data-testid={`calendar-rdv-${r.prospect_id}`}>
                        {r.rdv_heure || '—'} · {r.enseigne || r.nom_magasin || r.ville}
                      </button>
                    ))}
                    {c.rdvs.length > 3 && (
                      <p className="text-[9px] text-[#71717A] px-1">+{c.rdvs.length - 3} autres</p>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
        {loading && <p className="text-xs text-[#71717A] text-center mt-3">Chargement…</p>}
        {!loading && items.filter(r => r.rdv_date_iso).length === 0 && (
          <p className="text-xs text-[#71717A] text-center mt-3">Aucun RDV avec date pour cette période.</p>
        )}
      </CardContent>
    </Card>
  );
}

function EmailBadge({ row }) {
  const recipients = row.email_recipients || [];
  const cc = row.email_cc || [];
  const all = [...recipients, ...cc];
  if (row.email_status === 'sent') {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge className="bg-[#DCFCE7] text-[#15803D] border border-[#86EFAC] gap-1 cursor-help" data-testid="email-badge-sent">
              <MailCheck className="w-3 h-3" /> Envoyé
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-md">
            <div className="text-xs space-y-1">
              <p className="font-semibold">Email envoyé le {formatSentAt(row.email_sent_at)}</p>
              {row.email_author && <p className="text-[#A1A1AA]">Par {row.email_author}</p>}
              <p><strong>À :</strong> {recipients.join(', ') || '—'}</p>
              {cc.length > 0 && <p><strong>Copie :</strong> {cc.join(', ')}</p>}
              <p className="text-[#A1A1AA] pt-1">{all.length} destinataire{all.length > 1 ? 's' : ''} au total</p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  if (row.email_status === 'error') {
    return (
      <Badge className="bg-[#FEE2E2] text-[#B91C1C] border border-[#FCA5A5] gap-1" data-testid="email-badge-error">
        <MailX className="w-3 h-3" /> Échec
      </Badge>
    );
  }
  return (
    <Badge className="bg-[#F5F5F4] text-[#71717A] border border-[#E5E5E4] gap-1" data-testid="email-badge-none">
      <MailWarning className="w-3 h-3" /> Non envoyé
    </Badge>
  );
}

export default function RdvPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [period, setPeriod] = useState('upcoming');
  const [commercialId, setCommercialId] = useState('all');
  const [commercials, setCommercials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [drawerProspect, setDrawerProspect] = useState(null);
  const [view, setView] = useState('table'); // 'table' | 'calendar'

  const load = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (commercialId && commercialId !== 'all') params.commercial_id = commercialId;
      const { data } = await api.get('/rdv', { params });
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/commercials');
        setCommercials(data || []);
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, commercialId]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { period };
      if (commercialId && commercialId !== 'all') params.commercial_id = commercialId;
      const resp = await api.get('/rdv/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rdv_pris_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  const stats = useMemo(() => {
    const sent = items.filter(r => r.email_status === 'sent').length;
    const err = items.filter(r => r.email_status === 'error').length;
    const none = items.filter(r => r.email_status === 'none').length;
    return { sent, err, none };
  }, [items]);

  return (
    <div className="space-y-6" data-testid="rdv-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <Calendar className="w-6 h-6 text-[#F97316]" />
            RDV pris
          </h1>
          <p className="text-sm text-[#71717A] mt-1">
            {count} rendez-vous {period !== 'all' ? `(${PERIOD_OPTIONS.find(p => p.value === period)?.label.toLowerCase()})` : ''}
            {' · '}<span className="text-[#15803D]">{stats.sent} email envoyé{stats.sent > 1 ? 's' : ''}</span>
            {stats.err > 0 && <span className="text-[#B91C1C]"> · {stats.err} échec{stats.err > 1 ? 's' : ''}</span>}
            {stats.none > 0 && <span className="text-[#71717A]"> · {stats.none} sans email</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="inline-flex rounded-md border border-[#E5E7EB] overflow-hidden" data-testid="view-toggle">
            <button onClick={() => setView('table')}
              className={`px-3 py-1.5 text-xs font-medium ${view === 'table' ? 'bg-[#F97316] text-white' : 'bg-white text-[#71717A] hover:bg-[#FAFAFA]'}`}
              data-testid="view-table-btn">
              <List className="w-3.5 h-3.5 inline mr-1" /> Tableau
            </button>
            <button onClick={() => setView('calendar')}
              className={`px-3 py-1.5 text-xs font-medium ${view === 'calendar' ? 'bg-[#F97316] text-white' : 'bg-white text-[#71717A] hover:bg-[#FAFAFA]'}`}
              data-testid="view-calendar-btn">
              <LayoutGrid className="w-3.5 h-3.5 inline mr-1" /> Calendrier
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reload-rdv-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
          <Button size="sm" onClick={handleExport} disabled={exporting || items.length === 0}
            className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="export-rdv-btn">
            <Download className="w-4 h-4 mr-1" /> {exporting ? 'Export...' : 'Excel'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-[#E5E7EB]">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px]">
            <p className="text-xs font-medium text-[#71717A] mb-1">Période</p>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="h-9" data-testid="period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <p className="text-xs font-medium text-[#71717A] mb-1">Commercial</p>
            <Select value={commercialId} onValueChange={setCommercialId}>
              <SelectTrigger className="h-9" data-testid="commercial-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {commercials.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.prenom} {c.nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Calendar OR Table */}
      {view === 'calendar' ? (
        <CalendarView items={items} loading={loading} onOpen={(pid) => setDrawerProspect({ id: pid })} />
      ) : (
      <Card className="border-[#E5E7EB]">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-sm text-[#71717A]">Chargement...</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-sm text-[#71717A]">
              Aucun RDV trouvé sur cette période.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] border-b border-[#E5E7EB]">
                  <tr className="text-left text-[11px] uppercase tracking-wide text-[#71717A]">
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Heure</th>
                    <th className="px-4 py-3 font-medium">Magasin</th>
                    <th className="px-4 py-3 font-medium">Ville</th>
                    <th className="px-4 py-3 font-medium">Dirigeant</th>
                    <th className="px-4 py-3 font-medium">Téléphone</th>
                    <th className="px-4 py-3 font-medium">Commercial</th>
                    <th className="px-4 py-3 font-medium">Email RDV</th>
                    <th className="px-4 py-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(r => (
                    <tr key={r.prospect_id} className="border-b border-[#F4F4F5] hover:bg-[#FFF7ED]/40 transition-colors"
                      data-testid={`rdv-row-${r.prospect_id}`}>
                      <td className="px-4 py-3 font-medium whitespace-nowrap">{formatRdvDate(r.rdv_date_iso || r.rdv_date)}</td>
                      <td className="px-4 py-3 text-[#52525B] whitespace-nowrap">{r.rdv_heure || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#18181B] flex items-center gap-2">
                          {r.enseigne} {r.nom_magasin}
                          {r.status_changed_after_rdv && (
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex items-center" data-testid={`status-changed-${r.prospect_id}`}>
                                    <AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B]" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs text-xs">
                                  RDV email envoyé, mais le statut a changé depuis : <strong>{r.statut_commercial}</strong>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <div className="text-xs text-[#71717A]">{r.rue}</div>
                      </td>
                      <td className="px-4 py-3 text-[#52525B] whitespace-nowrap">{r.code_postal} {r.ville}</td>
                      <td className="px-4 py-3 text-[#52525B] whitespace-nowrap">{r.dirigeant || '—'}</td>
                      <td className="px-4 py-3 text-[#52525B] whitespace-nowrap font-mono text-xs">
                        {r.telephone ? (
                          <a href={`tel:${r.telephone}`} className="hover:text-[#0055FF] inline-flex items-center gap-1">
                            <Phone className="w-3 h-3" />{r.telephone}
                          </a>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {r.commercial_nom ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                            <span className="w-2 h-2 rounded-full" style={{ background: r.commercial_color }}></span>
                            {r.commercial_nom}
                          </span>
                        ) : <span className="text-[#A1A1AA] text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3"><EmailBadge row={r} /></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#8B5CF6] hover:bg-[#F5F3FF]"
                          onClick={() => setDrawerProspect({ id: r.prospect_id })}
                          title="Modifier la fiche (enseigne, contact, statut…)"
                          data-testid={`edit-prospect-${r.prospect_id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => navigate(`/prospects?prospect=${r.prospect_id}`)}
                          title="Ouvrir la fiche complète"
                          data-testid={`open-prospect-${r.prospect_id}`}>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}
      {/* Embedded drawer for in-place editing */}
      {drawerProspect && (
        <ProspectDrawer
          prospect={drawerProspect}
          onClose={() => setDrawerProspect(null)}
          onUpdate={load}
          commerciaux={commercials}
        />
      )}
    </div>
  );
}
